import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Input,
  ModalFooter,
  Button,
  Text,
  Checkbox,
  Box,
} from "@chakra-ui/react";
import { useOrdersStore } from "../store/useOrdersStore";
import { useEffect, useState } from "react";
import services from "../services";
import { useGaraazToastWithChildren } from "../../../utils/useNotification";
import { OrderHandlerAllocationType } from "../enums";

type OrderReturnModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  orderData: any;
  refetchAllOrders?: () => void;
};

const OrderReturnModal: React.FC<OrderReturnModalProps> = ({
  isOpen,
  onClose,
  orderId,
  orderData,
  refetchAllOrders,
}) => {
  const garaazToast = useGaraazToastWithChildren();
  const orderItems = useOrdersStore((state) => state.orderItems[orderId]);
  const [newOrderItems, setNewOrderItems] = useState(
    orderItems.map((item: any) => {
      return { ...item, orderedQuantity: 0 };
    })
  );
  const [returnReason, setReturnReason] = useState("");
  const [orderItemErrors, setOrderItemErrors] = useState<string[]>([]);

  useEffect(() => {
    setNewOrderItems(
      orderItems.map((item: any) => {
        return { ...item, orderedQuantity: 0 };
      })
    );
    setOrderItemErrors(orderItems.map((item: any) => ""));
  }, [orderItems]);

  async function onReturnButtonClick() {
    try {
      const returnItems = newOrderItems
        .filter((item: any) => item.orderedQuantity > 0)
        .map((item: any) => ({
          partId: item.partId,
          returnQuantity: item.orderedQuantity
        }));

      if (returnItems.length === 0) {
        garaazToast({
          title: "No items selected for return",
          status: "warning",
        });
        return;
      }

      const response = await services.createReturnOrder(
        orderData.orderNo,
        returnItems,
        returnReason
      );

      const renderSuccessToast = (
        <div
          style={{ display: "flex", flexDirection: "column", color: "white" }}
        >
          {response.data && (
            <div>
              {response.data.virtualOrderNo && (
                <p>Virtual Order created: {response.data.virtualOrderNo}</p>
              )}
              {response.data.returnOrderNo && (
                <p>Return Order created: {response.data.returnOrderNo}</p>
              )}
            </div>
          )}
        </div>
      );

      garaazToast({
        title: "Return Order Created",
        render: renderSuccessToast,
        status: "success",
      });

      !!refetchAllOrders && refetchAllOrders();
      onClose();
    } catch (error: any) {
      
      let errorMessage = "Failed to create return order";
      
      if (error?.response?.data?.errorMsg && Array.isArray(error.response.data.errorMsg)) {
        errorMessage = error.response.data.errorMsg.join(", ");
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.errorMsg && typeof error.response.data.errorMsg === 'string') {
        errorMessage = error.response.data.errorMsg;
      }
      
      const renderErrorToast = (
        <div
          style={{ display: "flex", flexDirection: "column", color: "white" }}
        >
          <div>
            <p>{errorMessage}</p>
          </div>
        </div>
      );
      
      garaazToast({
        title: "Failed to Create Return Order",
        render: renderErrorToast,
        status: "error",
      });
    }
  }

  function onReturnedOrderItemsQuantityChange(
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    orderItem: any
  ) {
    setNewOrderItems((prev: any) => {
      let newOrderItemsTemp = prev.map((item: any, idx: number) => {
        if (idx === index) {
          return {
            ...item,
            orderedQuantity: parseInt(e.target.value) || 0,
            givenQuantity: parseInt(e.target.value) || 0,
          };
        }
        return item;
      });

      return newOrderItemsTemp;
    });

    const remainingQuantity = orderItem.orderedQuantity - (orderItem.returnedQuantity || 0);
    
    const inputValue = parseInt(e.target.value);
    if (
      (e.target.value !== "" && (isNaN(inputValue) || inputValue > remainingQuantity || inputValue < 0))
    ) {
      setOrderItemErrors((prev) => {
        let newOrderItemErrors = prev.map((item: any, idx: number) => {
          return index === idx
            ? "Can not be greater than remaining quantity"
            : item;
        });
        return newOrderItemErrors;
      });
    } else {
      setOrderItemErrors((prev) => {
        let newOrderItemErrors = prev.map((item: any, idx: number) =>
          index === idx ? "" : item
        );
        return newOrderItemErrors;
      });
    }
  }

  return (
    <>
      <Modal
        returnFocusOnClose={false}
        isOpen={isOpen}
        onClose={onClose}
        size={
          orderItems.some((item: any) => (item.returnedQuantity || 0) > 0) ? "4xl" : "2xl"
        }
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Return Order
            <Text fontStyle={"italic"} fontSize={"xs"}>
              This Action will create a new Order.
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody display={"flex"} flexDirection={"column"}>
            <Box mb={4}>
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                Return Reason:
              </Text>
              <Input
                placeholder="Enter reason for return..."
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
              />
            </Box>

            <TableContainer>
              <Table variant="striped">
                <Thead>
                  <Tr>
                    <Th>Part Number</Th>
                    <Th isNumeric>Ordered Quantity</Th>
                    {orderItems.some((item: any) => (item.returnedQuantity || 0) > 0) && (
                      <Th isNumeric>Already Returned Quantity</Th>
                    )}
                    <Th isNumeric>Return Quantity</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orderItems.length > 0 &&
                    Object.entries(orderItems[0])?.length !== 0 &&
                    orderItems?.map((orderItem: any, index: number) => {
                      const remainingQuantity = orderItem.orderedQuantity - (orderItem.returnedQuantity || 0);
                      const hasReturnedItems = orderItems.some((item: any) => (item.returnedQuantity || 0) > 0);
                      
                      return (
                        <Tr key={index}>
                          <Td
                            paddingTop={"3"}
                            paddingBottom={"3"}
                            fontSize={"sm"}
                          >
                            {orderItem.partNumber}
                          </Td>
                          <Td
                            paddingTop={"3"}
                            paddingBottom={"3"}
                            textAlign={"center"}
                          >
                            {orderItem.orderedQuantity}
                          </Td>
                          {hasReturnedItems && (
                            <Td
                              paddingTop={"3"}
                              paddingBottom={"3"}
                              textAlign={"center"}
                            >
                              {orderItem.returnedQuantity || 0}
                            </Td>
                          )}
                          <Td
                            paddingTop={"3"}
                            textAlign={"center"}
                            paddingBottom={
                              orderItemErrors[index] === "" ? "3" : "0"
                            }
                          >
                            <Input
                              size={"xs"}
                              outline={"0.2px solid grey"}
                              value={newOrderItems[index]?.orderedQuantity === 0 ? "" : newOrderItems[index]?.orderedQuantity}
                              onChange={(e) => {
                                onReturnedOrderItemsQuantityChange(
                                  e,
                                  index,
                                  orderItem
                                );
                              }}
                              width={"35%"}
                              maxWidth={"5rem"}
                              type="number"
                              min="0"
                              max={remainingQuantity}
                              isDisabled={remainingQuantity === 0}
                            />
                            <Text color={"red"} fontSize={"2xs"}>
                              {orderItemErrors[index] !== "" &&
                                orderItemErrors[index]}
                            </Text>
                          </Td>
                        </Tr>
                      );
                    })}
                </Tbody>
              </Table>
            </TableContainer>

            <Checkbox
              mt={5}
              alignSelf={"end"}
              color={"#4a5567"}
              isChecked={newOrderItems?.every(
                (item: any, index: number) => {
                  const remainingQuantity = orderItems[index]?.orderedQuantity - (orderItems[index]?.returnedQuantity || 0);
                  return item?.orderedQuantity === remainingQuantity;
                }
              )}
              onChange={(e) => {
                if (e.target.checked) {
                  setNewOrderItems(
                    orderItems.map((item: any) => {
                      const remainingQuantity = item.orderedQuantity - (item.returnedQuantity || 0);
                      return {
                        ...item,
                        orderedQuantity: remainingQuantity,
                      };
                    })
                  );
                } else {
                  setNewOrderItems(
                    orderItems.map((item: any) => {
                      return {
                        ...item,
                        orderedQuantity: 0,
                      };
                    })
                  );
                }
              }}
            >
              Return All Items
            </Checkbox>
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose} mr={3}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={onReturnButtonClick}
              isDisabled={
                newOrderItems.some(
                  (item: any, index: number) => {
                    const remainingQuantity = orderItems[index]?.orderedQuantity - (orderItems[index]?.returnedQuantity || 0);
                    return (
                      isNaN(item?.orderedQuantity) ||
                      item?.orderedQuantity > remainingQuantity ||
                      item?.orderedQuantity < 0
                    );
                  }
                ) ||
                newOrderItems.every(
                  (item: any) => item.orderedQuantity === 0
                )
              }
            >
              Return Order
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default OrderReturnModal;
